import { areObjectIdsEqual } from 'helpers/idComparison';
import { useEffect, useState } from 'react';
import { logError } from 'setup/logger/logError';
import { isChannelRequestTimeout } from 'shared/channels';
import { documentationClient } from 'shared/domain/documentation/documentationClient';
import { mayChangeShownVersion } from 'shared/domain/documentation/documentationEvents';
import type { DocumentationModel } from 'shared/domain/documentation/documentationModel';
import { ReadFreshness } from 'shared/utils/singleFlight';
import { fetchDocumentationDeduped } from 'views/issue/localization/helpers';

export interface MapDocumentationRead {
  /** `undefined` when there is no map, or its documentation cannot be read (not stored on the device). */
  documentation: DocumentationModel | undefined;
  /** `false` while the documentation is read for the first time, also in the render that switches to another one. */
  settled: boolean;
}

/** A read with the documentation it was made for, so it is never handed out for another one. */
interface DocumentationReadFor {
  documentationId: string;
  documentation: DocumentationModel | undefined;
}

const NO_MAP: MapDocumentationRead = { documentation: undefined, settled: true };
const READING: MapDocumentationRead = { documentation: undefined, settled: false };

/**
 * A map's documentation as stored on the device, kept current (PT-7117): a new current
 * version or a withdrawal comes with the documentation, not with the level or the issue,
 * so it is read again after the documentation events that may change it.
 * - a read after such an event does not share a read started before it;
 * - only the latest read is kept, so an older one that comes back later does not replace it;
 * - a read that fails leaves the documentation unknown; the failure is logged, unless it is
 *   a timeout, which the channel layer reports.
 */
export function useMapDocumentation(documentationId: string | undefined): MapDocumentationRead {
  const [lastRead, setLastRead] = useState<DocumentationReadFor | undefined>(undefined);

  useEffect(() => {
    if (!documentationId) {
      return;
    }
    let cancelled = false;
    let latestReadNumber = 0;
    const read = (freshness: ReadFreshness): void => {
      latestReadNumber += 1;
      const readNumber = latestReadNumber;
      fetchDocumentationDeduped(documentationId, freshness)
        .catch((error: unknown) => {
          // A timeout is reported by the channel layer itself, with the worker's diagnostics.
          if (!isChannelRequestTimeout(error)) {
            logError('Reading the map documentation failed', { error, documentationId });
          }
          return undefined;
        })
        .then((documentation) => {
          if (!cancelled && readNumber === latestReadNumber) {
            setLastRead({ documentationId, documentation });
          }
        });
    };
    read(ReadFreshness.sharedWithRunning);
    const unsubscribe = documentationClient.onEvent((event) => {
      if (mayChangeShownVersion(event)) {
        read(ReadFreshness.startedAfterChange);
      }
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [documentationId]);

  if (!documentationId) {
    return NO_MAP;
  }
  return lastRead && areObjectIdsEqual(lastRead.documentationId, documentationId)
    ? { documentation: lastRead.documentation, settled: true }
    : READING;
}
